'use client';

/**
 * LiveActivity — the laboratory's pulse, bottom-right. A collapsed pill
 * expands into a scrollable feed of LabEvents from the world store, fed by
 * the /api/lab/events stream (SSE). Honesty contract: simulated rows carry a
 * visible [SIM] suffix; only `kind: 'real'` rows are marked ·real.
 *
 * The stream also carries named `status` and `roster` frames (Genesis-Live
 * bridge, L6b). This component owns the one EventSource and forwards both
 * into the world store: `status` → freshness/runId/epoch/mode/counters,
 * `roster` → the 3D field's agent population (via `setAgents`, which
 * itself only replaces the roster when the content actually changed). The
 * 3D field (AgentSpace.tsx) and any future evidence panel read the store,
 * never the stream directly.
 *
 * A frame this browser REFUSES (a roster that fails `validateRoster`, a
 * `status` that fails `parseStatusFrame`) is reported, not swallowed. The
 * store keeping its last good state is correct; hiding the refusal is not —
 * a page stuck reading OFFLINE because of a rejected frame looked exactly
 * like a real observer outage, and neither the visitor nor whoever operates
 * the bridge could tell them apart. `bridgeFault` surfaces it in the pill
 * and in the panel, and the next accepted frame clears it.
 */

import { useEffect, useRef, useState } from 'react';
import { useWorld } from '../../lab/store';
import { ZONES, type LabEvent, type ZoneId } from '../../lab/types';
import { parseStatusFrame } from '../../lab/parse-status-frame';
import { validateRoster } from '../../lab/live-validate';
import { displayFreshness, freshnessSuffix, showsCanary } from '../../lab/freshness-label';
import { useLang } from '../../i18n/client';
import { getDict } from '../../i18n/dictionary';
import s from './LiveActivity.module.css';

const RETRY_INITIAL_MS = 1000;
const RETRY_CAP_MS = 30000;

function parseLabEvent(data: string): LabEvent | null {
  try {
    const raw: unknown = JSON.parse(data);
    if (typeof raw !== 'object' || raw === null) return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.message !== 'string' || o.message.length === 0) return null;
    const zone =
      typeof o.zone === 'string' && (ZONES as string[]).includes(o.zone)
        ? (o.zone as ZoneId)
        : undefined;
    return {
      id:
        typeof o.id === 'string' && o.id.length > 0
          ? o.id
          : `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ts: typeof o.ts === 'number' && Number.isFinite(o.ts) ? o.ts : Date.now(),
      source: typeof o.source === 'string' && o.source.length > 0 ? o.source : 'LAB',
      type: typeof o.type === 'string' ? o.type : 'event',
      message: o.message,
      // Never upgrade an unlabeled event to 'real' — honesty by default.
      kind: o.kind === 'real' ? 'real' : 'simulated',
      zone,
    };
  } catch {
    return null;
  }
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function LiveActivity() {
  const t = getDict(useLang()).live;
  const events = useWorld((w) => w.events);
  const rosterLive = useWorld((w) => w.rosterLive);
  const freshness = useWorld((w) => w.freshness);
  const liveMode = useWorld((w) => w.liveMode);
  const bridgeFault = useWorld((w) => w.bridgeFault);
  const displayed = displayFreshness(rosterLive, freshness);
  const canary = showsCanary(displayed, liveMode);
  // The dot's colour is part of the honesty signal too — a green "online"
  // glow next to STALE/OFFLINE text would contradict the words beside it.
  const dotClass =
    displayed === 'STALE' ? s.pillDotStale : displayed === 'OFFLINE' ? s.pillDotOffline : displayed === 'SYNCING' ? s.pillDotSyncing : '';
  const [expanded, setExpanded] = useState(false);
  /** Ids already present when the panel was opened — these never flash. */
  const seenAtOpen = useRef<Set<string>>(new Set());

  /* ── SSE subscription with capped backoff ────────────────── */
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;

    let es: EventSource | null = null;
    let retryMs = RETRY_INITIAL_MS;
    let timer: number | undefined;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      es = new EventSource('/api/lab/events');
      es.onopen = () => {
        retryMs = RETRY_INITIAL_MS;
      };
      es.onmessage = (ev: MessageEvent<string>) => {
        const e = parseLabEvent(ev.data);
        if (e) useWorld.getState().pushEvent(e);
      };
      // Named frames introduced by the Genesis-Live bridge (L6b) — named
      // SSE events never reach `onmessage`, so each gets its own listener.
      // Both are re-validated here (defense-in-depth: this is the site's
      // own stream, but never trusted blindly) before touching the store.
      // A refused frame is never swallowed. The store keeps whatever it
      // already had — that part is right — but a silent catch left the page
      // reading OFFLINE forever with no way for a visitor to tell a rejected
      // frame from a real outage. `bridgeFault` puts it on screen instead,
      // and the next good frame clears it (see store.ts `setLiveStatus`).
      es.addEventListener('status', (ev: MessageEvent<string>) => {
        let status = null;
        try {
          status = parseStatusFrame(JSON.parse(ev.data) as unknown);
        } catch {
          status = null;
        }
        if (status) useWorld.getState().setLiveStatus(status);
        else useWorld.getState().setBridgeFault('status');
      });
      es.addEventListener('roster', (ev: MessageEvent<string>) => {
        try {
          const raw: unknown = JSON.parse(ev.data);
          const roster = validateRoster(raw);
          useWorld.getState().setAgents(roster);
          useWorld.getState().setBridgeFault(null);
        } catch {
          useWorld.getState().setBridgeFault('roster');
        }
      });
      es.onerror = () => {
        es?.close();
        es = null;
        if (disposed) return;
        timer = window.setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, RETRY_CAP_MS);
      };
    };

    connect();
    return () => {
      disposed = true;
      if (timer !== undefined) window.clearTimeout(timer);
      es?.close();
    };
  }, []);

  const toggle = () => {
    setExpanded((open) => {
      if (!open) {
        seenAtOpen.current = new Set(useWorld.getState().events.map((e) => e.id));
      }
      return !open;
    });
  };

  return (
    <div className={s.root}>
      {expanded && (
        <section
          id="lab-feed-panel"
          className={s.panel}
          aria-label="Laboratory activity feed"
        >
          <header className={s.panelHead}>
            <span className={`machine--sm ${s.panelTitle}`}>{t.label}</span>
            <span className={`machine--sm ${s.panelNote}`}>
              {displayed === 'OFFLINE'
                ? t.simulationLabel
                : displayed === 'SYNCING'
                  ? t.unknownLabel
                  : displayed}
              {canary ? ` · ${t.canaryLabel}` : ''} · {t.realEventsMarked} ·{t.real}
            </span>
          </header>
          {bridgeFault !== null && (
            <p className={`machine--sm ${s.fault}`} role="status" aria-label={t.faultAria}>
              {t.faultLabel} — {bridgeFault === 'roster' ? t.faultRosterRejected : t.faultStatusRejected}
            </p>
          )}
          <ol className={s.list} aria-live="off">
            {events.length === 0 && (
              <li className={`machine--sm ${s.empty}`}>{t.awaiting}</li>
            )}
            {events.map((e) => (
              <li
                key={e.id}
                className={`${s.row} ${
                  seenAtOpen.current.has(e.id) ? '' : s.rowFresh
                }`}
              >
                <span className={s.time}>{fmtTime(e.ts)}</span>
                <span className={s.dotSep} aria-hidden="true">
                  ·
                </span>
                <span className={s.source}>{e.source}</span>
                <span className={s.dotSep} aria-hidden="true">
                  ·
                </span>
                <span className={s.message}>
                  {e.message}
                  {e.kind === 'simulated' ? (
                    <span className={s.sim}> [SIM]</span>
                  ) : (
                    <span className={s.real}> ·{t.real}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <button
        type="button"
        className={`${s.pill} ${expanded ? s.pillOpen : ''}`}
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls="lab-feed-panel"
        aria-label={expanded ? t.collapseAria : t.expandAria}
      >
        <span className={`status-dot ${s.pillDot} ${dotClass}`} aria-hidden="true" />
        <span className={s.pillText}>
          {t.label} {freshnessSuffix(displayed, liveMode)}
          {bridgeFault !== null ? ` · ${t.faultLabel}` : ''}
        </span>
      </button>
    </div>
  );
}
